import axios, { AxiosError } from 'axios';
import attachAuthToken from './axiosConfig';

// Modelo de regra com suporte a acentuação conforme backend
export type ModeloRegra = 'VITALICIO' | 'BONIFICAÇÕES' | 'DESCONTOS' | 'PERSONALIZADA';
export type EscopoRegra = 'LIDER' | 'EQUIPE' | 'AMBOS';
export type AlvoDebito = 'PAI' | 'FILHO';
export type FonteCalculo = 'PAI' | 'FILHO' | 'AMBOS';
export type OperacaoRegra = 'CREDITO' | 'DEBITO';
export type MultiplicadorRegra = 'POR_BOLETO' | 'FIXO_MENSAL';
export type TipoCalculoRegra = 'PERCENTO' | 'VALOR';
export type QualificadoTipo = 'REGIONAL' | 'COOPERATIVA' | 'VOLUNTARIO';

export interface ListRegrasQuery {
  page?: number;
  limit?: number;
  qualificado_tipo?: QualificadoTipo;
  qualificado_uuid?: string;
  // Filtragem por modelo de regra (aba atual)
  modelo_regra?: ModeloRegra;
  // Período selecionado (mm-yyyy) para filtros gerais
  periodo?: string;
  referencia_inicio?: string; // YYYY-MM-DD
  referencia_fim?: string | null; // YYYY-MM-DD ou null
}

export interface CreateRegraDto {
  // Qualificação
  qualificado_tipo: QualificadoTipo;
  qualificado_uuid: string;

  // Abas de regras (modelo)
  modelo_regra: ModeloRegra;

  // Tipo de cálculo da regra
  tipo_regra: TipoCalculoRegra; // Percento ou Valor

  // Referências de contrato
  contrato_inicio?: string; // opcional
  contrato_fim?: string | null; // opcional

  // Validade da regra
  validade_inicio: string; // obrigatório
  validade_fim: string | null; // pode ser nulo

  // Valores
  valor: number;
  meta?: number; // inteiro opcional

  // Observações
  observacoes?: string;

  // Campos exclusivos para PERSONALIZADA
  escopo?: EscopoRegra;
  alvo_debito?: AlvoDebito;
  fonte_calculo?: FonteCalculo;
  operacao?: OperacaoRegra;
  multiplicador?: MultiplicadorRegra;
}

export interface UpdateRegraDto extends Partial<CreateRegraDto> {}

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

attachAuthToken(api);

const PATH = '/comissionamentos/regras';

export async function listRegras(params: ListRegrasQuery) {
  const p = { ...params };
  try {
    const { data } = await api.get(PATH, { params: p });
    return data;
  } catch (e) {
    const err = e as AxiosError<any>;
    throw new Error(err.response?.data?.message || 'Erro ao listar regras');
  }
}

export async function createRegra(body: CreateRegraDto) {
  const payload = { ...body, validade_fim: body.validade_fim ?? null, contrato_fim: body.contrato_fim ?? null };
  try {
    const { data } = await api.post(PATH, payload);
    return data;
  } catch (e) {
    const err = e as AxiosError<any>;
    throw new Error(err.response?.data?.message || 'Erro ao criar regra');
  }
}

export async function getRegra(id: string) {
  try {
    const { data } = await api.get(`${PATH}/${id}`);
    return data;
  } catch (e) {
    const err = e as AxiosError<any>;
    throw new Error(err.response?.data?.message || 'Erro ao obter regra');
  }
}

export async function updateRegra(id: string, body: UpdateRegraDto) {
  const payload = { ...body };
  if ('validade_fim' in payload && payload.validade_fim === '') (payload as any).validade_fim = null;
  if ('contrato_fim' in payload && payload.contrato_fim === '') (payload as any).contrato_fim = null;
  try {
    const { data } = await api.patch(`${PATH}/${id}`, payload);
    return data;
  } catch (e) {
    const err = e as AxiosError<any>;
    throw new Error(err.response?.data?.message || 'Erro ao atualizar regra');
  }
}

export async function deleteRegra(id: string) {
  try {
    const { data } = await api.delete(`${PATH}/${id}`);
    return data;
  } catch (e) {
    const err = e as AxiosError<any>;
    throw new Error(err.response?.data?.message || 'Erro ao remover regra');
  }
}